export interface CountryInfo {
  country: string;
  countryCode: string;
}

export const UNKNOWN_COUNTRY: CountryInfo = { country: 'Unknown', countryCode: 'XX' };

const COUNTRY_REGION_RE = /-([A-Z]{2})\b/i;

let cached: CountryInfo | null = null;

export function isValidCountryInfo(info: CountryInfo): boolean {
  return info.country !== 'Unknown' && info.countryCode !== 'XX' && info.countryCode.length === 2;
}

export function getCountryName(code: string): string {
  try {
    const display = new Intl.DisplayNames(['en'], { type: 'region' });
    return display.of(code) ?? code;
  } catch {
    return code;
  }
}

export function getCountryCode(language: string | undefined = navigatorLanguage()): string {
  if (!language) return 'XX';
  const match = COUNTRY_REGION_RE.exec(language);
  if (match?.[1]) return match[1].toUpperCase();
  return 'XX';
}

export function getCountryInfo(language: string | undefined = navigatorLanguage()): CountryInfo {
  if (cached && isValidCountryInfo(cached)) return cached;
  const code = getCountryCode(language);
  if (code === 'XX') return UNKNOWN_COUNTRY;
  const info: CountryInfo = { country: getCountryName(code), countryCode: code };
  cached = info;
  return info;
}

export function _resetCountryCache(): void {
  cached = null;
}

export function flagEmoji(countryCode: string): string {
  const code = countryCode.toUpperCase();
  if (code.length !== 2 || !/^[A-Z]{2}$/.test(code)) return '🏳';
  const A = 0x1f1e6;
  const a = 'A'.charCodeAt(0);
  return String.fromCodePoint(A + (code.charCodeAt(0) - a), A + (code.charCodeAt(1) - a));
}

/**
 * URL of a Twemoji SVG flag for the given country code, or null if the code
 * isn't a valid 2-letter region. Loaded via jsDelivr's CDN — keeps the bundle
 * cost at zero and renders flags consistently on Windows (which lacks a
 * native colour-emoji font for regional indicators).
 */
export function flagSrc(countryCode: string): string | null {
  const code = countryCode.toUpperCase();
  if (code.length !== 2 || !/^[A-Z]{2}$/.test(code)) return null;
  const A = 0x1f1e6;
  const a = 'A'.charCodeAt(0);
  const cp = [code.charCodeAt(0) - a, code.charCodeAt(1) - a]
    .map((offset) => (A + offset).toString(16))
    .join('-');
  return `https://cdn.jsdelivr.net/gh/twitter/twemoji@v14.0.2/assets/svg/${cp}.svg`;
}

function navigatorLanguage(): string | undefined {
  if (typeof navigator === 'undefined') return undefined;
  return navigator.language;
}
